import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useIsAdmin } from "../hooks/useFeatureFlags";

/**
 * In-app documentation viewer.
 *
 * SOURCE OF TRUTH: the documentation is written and versioned in the separate
 * `depository-docs-site` repository, published at DOCS_SITE_URL. What this page renders is the
 * copy bundled with the app (`public/docs/`), which exists so on-premise and air-gapped
 * installations still have the guide without reaching the internet. It is refreshed from
 * docs-site with `npm run docs:pull` — never edited here.
 *
 * The banner below always points at the canonical site, so a reader on a bundled copy can get to
 * the current version in one click. Override the host per installation with
 * REACT_APP_DOCS_SITE_URL when a customer mirrors the docs somewhere of their own.
 */
const DOCS_SITE_URL =
  process.env.REACT_APP_DOCS_SITE_URL || "https://depository-docs-site.vercel.app/";

const TABS = [
  { key: "user",     label: "User Guide",    src: "/docs/index.html" },
  { key: "api",      label: "API Reference", src: "/docs/dev/index.html" },
  { key: "internal", label: "Internal Docs", src: "/docs/dev/team.html", adminOnly: true },
];

const Documentation = () => {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const visibleTabs = TABS.filter(t2 => !t2.adminOnly || isAdmin);
  const [active, setActive] = useState(visibleTabs[0].key);
  const current = visibleTabs.find(tab => tab.key === active) || visibleTabs[0];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* Canonical-source banner */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        padding: "8px 16px", flexShrink: 0,
        background: "var(--c-surface-alt, #f5f7fb)",
        borderBottom: "1px solid var(--c-border, #e5e7eb)",
        fontSize: 12.5, color: "var(--c-text-secondary, #6b7280)",
      }}>
        <span>
          {t("docs.bundledNotice", {
            defaultValue:
              "This is the copy bundled with your installation. The documentation is maintained on the documentation site.",
          })}
        </span>
        <a
          href={DOCS_SITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6,
            padding: "5px 12px", borderRadius: 8, textDecoration: "none",
            fontWeight: 700, fontSize: 12.5,
            color: "#fff", background: "var(--c-accent, #3b82f6)",
            whiteSpace: "nowrap",
          }}
        >
          {t("docs.openLatest", { defaultValue: "Latest documentation" })}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </a>
      </div>

      {/* Tab bar */}
      <div style={{
        display: "flex", gap: 0,
        borderBottom: "1px solid var(--c-border, #e5e7eb)",
        background: "var(--c-surface, #fff)",
        paddingLeft: 16, flexShrink: 0,
      }}>
        {visibleTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActive(tab.key)}
            style={{
              padding: "10px 18px",
              fontSize: 13, fontWeight: active === tab.key ? 700 : 400,
              color: active === tab.key ? "var(--c-accent, #3b82f6)" : "var(--c-text-secondary, #6b7280)",
              background: "none", border: "none", borderBottom: active === tab.key
                ? "2px solid var(--c-accent, #3b82f6)"
                : "2px solid transparent",
              cursor: "pointer", transition: "color .15s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Iframe */}
      <iframe
        key={current.src}
        src={current.src}
        title={current.label}
        style={{ flex: 1, border: "none", display: "block", width: "100%" }}
      />
    </div>
  );
};

export default Documentation;
